import { useEffect, useState } from 'preact/hooks';
import { useSignalEffect } from '@preact/signals';
import { extractShareCode } from './lib/shareCode';
import { applySocialMeta } from './lib/socialMeta';
import { travellerName } from './lib/traveller';
import { t } from './i18n';
import { ensureSession } from './backend/session';
import { fetchRoute, verifyShareCode } from './backend/route';
import { connectSocket, disconnectSocket } from './backend/socket';
import { startRoutePoll, stopRoutePoll } from './backend/routePoll';
import { routeStore } from './state/routeStore';
import { LiveMap } from './components/LiveMap';
import { StatusHero } from './components/StatusHero';
import { BottomSheet } from './components/BottomSheet';
import { StatsTopRow, StatsAddresses } from './components/StatsCard';
import { WelcomeSheet } from './components/WelcomeSheet';
import { ArrivalState } from './components/ArrivalState';
import { EmergencyBanner } from './components/EmergencyBanner';
import { EmptyState } from './components/EmptyState';
import { DownloadAppCard } from './components/DownloadAppCard';
import { DownloadAppChip } from './components/DownloadAppChip';
import { ReplayButton } from './components/ReplayButton';
import { JourneyLog } from './components/JourneyLog';
import { LoadingScreen } from './components/LoadingScreen';

type BootState =
  | { stage: 'idle' }
  | { stage: 'no-code' }
  | { stage: 'session' }
  | { stage: 'verify' }
  | { stage: 'fetch'; routeId: string }
  | { stage: 'live'; routeId: string }
  | { stage: 'not-found' }
  | { stage: 'error'; message: string };

export function App() {
  const [boot, setBoot] = useState<BootState>({ stage: 'idle' });

  // Dynamic document.title — mirrors the legacy watcher's pattern
  // ("KommGutHeim – Begleite … auf dem Weg" / "… ist gut angekommen") but
  // localised. Re-runs on every route or locale signal change.
  useSignalEffect(() => {
    const route = routeStore.value;
    // Share tags track the same signals as the title — see lib/socialMeta.ts
    // for what they do and don't reach.
    applySocialMeta(route);

    const name = travellerName(route);
    if (!route || !name) {
      document.title = t('meta.titleDefault');
      return;
    }
    if (route.emergency?.state === 'active') {
      document.title = t('meta.titleEmergency', { name });
    } else if (route.finished) {
      document.title = t('meta.titleArrived', { name });
    } else {
      document.title = t('meta.titleTravelling', { name });
    }
  });

  useEffect(() => {
    const code = extractShareCode(window.location.pathname);
    if (!code) {
      setBoot({ stage: 'no-code' });
      return;
    }
    // Pre-fetch the MapLibre chunk in parallel with the API calls so the
    // map can mount without delay the moment route data arrives. Without
    // this the chunk wouldn't start downloading until <LiveMap /> mounts,
    // adding ~500ms of perceived latency on slow networks.
    void import('maplibre-gl');
    void runBoot(code, setBoot);
    return () => {
      disconnectSocket();
      stopRoutePoll();
    };
  }, []);

  if (boot.stage === 'no-code') return <EmptyState kind="no-code" />;
  if (boot.stage === 'not-found') return <EmptyState kind="not-found" />;
  if (boot.stage === 'error') return <EmptyState kind="error" message={boot.message} />;

  const route = routeStore.value;

  // Show the loading screen until we have a real location to centre the
  // map on. Gating on route alone isn't enough — a route doc can briefly
  // exist with no waypoints, and the map would flash a default centroid
  // before fitBounds jumps to the right place. Waiting for the first
  // waypoint means the map opens already showing the traveller.
  //
  // Exception: emergency-from-idle stub routes (api #143) can carry zero
  // waypoints, sometimes forever. The emergency subdoc has its own
  // location, so the banner and map can render without a single fix —
  // holding the loading screen here would hide an active emergency.
  const hasWaypoints = (route?.waypoints?.length ?? 0) > 0;
  if (!route || (!hasWaypoints && !route.emergency)) {
    return (
      <LoadingScreen
        stage={
          boot.stage === 'idle' || boot.stage === 'session' || boot.stage === 'verify' || boot.stage === 'fetch'
            ? boot.stage
            : 'fetch'
        }
      />
    );
  }

  const finished = route.finished === true;
  const emergencyActive = route.emergency?.state === 'active';

  return (
    <main class="relative h-dvh w-full overflow-hidden motion-safe:animate-[fadeIn_0.45s_ease-out]">
      <LiveMap />

      {/* Emergency banner is z-40 and overlays everything else when active.
          The component itself decides whether to render based on route.emergency. */}
      <EmergencyBanner />

      {/* Top bar — hidden during active emergency so the banner has full focus.
          Status (live / stale / offline / arrived / emergency) is rendered as
          a badge on the avatar inside the card, so the header is a single
          unified surface without a separate floating pill in the corner. */}
      {!emergencyActive && (
        <header class="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-center p-3">
          {finished ? <ArrivalState /> : <StatusHero />}
        </header>
      )}

      <BottomSheet
        initiallyOpen={true}
        collapsed={<StatsTopRow />}
        expanded={
          <div class="space-y-3">
            <StatsAddresses />
            {/* Journey log (#180) — a compact row that opens the full event
                history in its own view. Only renders when the route actually
                has describable events, so pre-#180 journeys keep the compact
                drawer. */}
            <JourneyLog />
            {/* Replay sits in the drawer (not the arrival hero) so the
                "they made it" message stays calm and the action lives
                next to the journey numbers it animates. Component is a
                no-op on active routes. */}
            <ReplayButton />
            {/* The card is only prominent once the journey is finished — at
                that point the watcher is in browse-mode and a bigger CTA
                isn't intrusive. During an active journey the chip below
                handles app-install discovery without taking drawer space. */}
            {finished && <DownloadAppCard />}
          </div>
        }
      />

      {/* Small persistent CTA at the bottom-right of the map. Dismissible. */}
      {!emergencyActive && <DownloadAppChip />}

      {/* Mount even after arrival so the drawer's "Watching as …" edit row
          can re-open it. EmergencyBanner still owns the page during an
          active emergency, so we don't compete for attention there. */}
      {!emergencyActive && <WelcomeSheet />}
    </main>
  );
}

async function runBoot(shareCode: string, set: (s: BootState) => void): Promise<void> {
  try {
    set({ stage: 'session' });
    await ensureSession();

    set({ stage: 'verify' });
    let routeId: string;
    try {
      routeId = await verifyShareCode(shareCode);
    } catch (error) {
      // /v3/route/verify returns 404-ish for unknown codes; surface that as a
      // distinct empty state rather than a generic error.
      if (error instanceof Error && /not.?found|404/i.test(error.message)) {
        set({ stage: 'not-found' });
        return;
      }
      throw error;
    }

    set({ stage: 'fetch', routeId });
    routeStore.value = await fetchRoute(routeId);

    set({ stage: 'live', routeId });
    connectSocket(shareCode, routeId);
    startRoutePoll(routeId);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'unknown error';
    set({ stage: 'error', message });
  }
}
